import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Wrench, SignOut, User, House, PlusCircle, Storefront, Coins, Users } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [credits, setCredits] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/credits")
      .then(({ data }) => setCredits(data))
      .catch((err) => console.warn("Failed to fetch credits for header badge:", err));
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-background text-foreground bg-grain flex flex-col">
      <header className="border-b border-border sticky top-0 z-40 bg-background/90 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="brand-link">
            <div className="w-9 h-9 rounded-md bg-primary flex items-center justify-center">
              <Wrench weight="bold" className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-display font-bold text-xl tracking-tight leading-none">DYNAMIC DIAG</div>
              <div className="text-[10px] font-mono-tech text-muted-foreground tracking-widest">AI DIAGNOSTIC ENGINE</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            <NavItem to="/dashboard" icon={<House weight="bold" className="w-4 h-4" />} label="Dashboard" testid="nav-dashboard" />
            <NavItem to="/diagnostic/new" icon={<PlusCircle weight="bold" className="w-4 h-4" />} label="New Diagnostic" testid="nav-new" />
            {credits?.is_shop && (
              <NavItem to="/team" icon={<Users weight="bold" className="w-4 h-4" />} label="Team" testid="nav-team" />
            )}
            {credits?.is_shop && credits?.plan && (
              <NavItem to="/settings" icon={<Storefront weight="bold" className="w-4 h-4" />} label="Shop" testid="nav-settings" />
            )}
            {credits?.is_shop && (
              <NavLink
              to="/billing"
              data-testid="nav-billing"
              className={({ isActive }) =>
                `px-3 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition-colors ${
                  isActive ? "bg-secondary text-white" : "text-muted-foreground hover:text-white hover:bg-secondary/60"
                }`
              }
            >
              <Coins weight="fill" className="w-4 h-4" />
              {credits?.plan_unlimited ? (
                <span className="text-primary font-mono-tech text-xs">∞</span>
              ) : credits ? (
                <span className={`font-mono-tech text-xs px-1.5 py-0.5 rounded ${
                  (credits.monthly_remaining ?? 0) + (credits.balance ?? 0) === 0
                    ? "bg-destructive/20 text-red-300"
                    : "bg-primary/15 text-primary"
                }`} data-testid="nav-credit-count">
                  {(credits.monthly_remaining ?? 0) + (credits.balance ?? 0)}
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">…</span>
              )}
              <span>Credits</span>
            </NavLink>
            )}
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-2 text-sm">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-7 h-7 rounded-full border border-border" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center">
                  <User weight="bold" className="w-4 h-4" />
                </div>
              )}
              <span className="text-muted-foreground" data-testid="user-name">{user?.name}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              data-testid="logout-btn"
              className="text-muted-foreground hover:text-white"
            >
              <SignOut weight="bold" className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-border mt-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground font-mono-tech">
          <span>
            For Questions/Concerns email{" "}
            <a href="mailto:Admin@JLDynamic.com" className="text-primary hover:underline" data-testid="app-footer-contact-email">Admin@JLDynamic.com</a>
          </span>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-white transition-colors" data-testid="app-footer-privacy">Privacy</Link>
            <span>© {new Date().getFullYear()} Dynamic Diag</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

function NavItem({ to, icon, label, testid }) {
  return (
    <NavLink
      to={to}
      data-testid={testid}
      className={({ isActive }) =>
        `px-3 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition-colors ${
          isActive ? "bg-secondary text-white" : "text-muted-foreground hover:text-white hover:bg-secondary/60"
        }`
      }
    >
      {icon} {label}
    </NavLink>
  );
}
